import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Scanner } from "@yudiel/react-qr-scanner";
import {
  ArrowPathIcon,
  ChartBarSquareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  QrCodeIcon,
  StopIcon,
} from "@heroicons/react/24/outline";
import MetaData from "../component/Metadata";
import ScanPreviewConfirmModal from "../component/ScanPreviewConfirmModal";

const getPayload = (response) => {
  const body = response?.data || {};
  return body?.data && body.valid === undefined ? body.data : body;
};

const readQrValue = (data) => {
  if (!data) return "";
  if (Array.isArray(data)) {
    return data[0]?.rawValue || (typeof data[0] === "string" ? data[0] : "");
  }
  if (typeof data === "object") return data.rawValue || data.text || "";
  return typeof data === "string" ? data : "";
};

export default function PwaScanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const scanLockRef = useRef(false);
  const resumeTimerRef = useRef(null);

  const [scannerActive, setScannerActive] = useState(false);
  const [scannerKey, setScannerKey] = useState(0);
  const [scannedValue, setScannedValue] = useState("");
  const [manualValue, setManualValue] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [lastResult, setLastResult] = useState(null);

  const content = location.state?.content || {};
  const contentId =
    searchParams.get("content_id") ||
    content.content_id ||
    content.contentId ||
    "";
  const contentType = String(
    searchParams.get("type") || content.type || content.contentType || "event",
  ).toLowerCase();
  const contentTitle = content.title || content.name || "";

  useEffect(
    () => () => {
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    },
    [],
  );

  const startScanner = () => {
    setCameraError("");
    setLastResult(null);
    scanLockRef.current = false;
    setScannerKey((current) => current + 1);
    setScannerActive(true);
  };

  const stopScanner = () => {
    setScannerActive(false);
    scanLockRef.current = false;
  };

  const openPreview = useCallback((value) => {
    const normalized = String(value || "").trim();
    if (!normalized || scanLockRef.current) return;

    scanLockRef.current = true;
    setScannerActive(false);
    setScannedValue(normalized);
    setPreviewOpen(true);
  }, []);

  const handleScan = useCallback(
    (data) => {
      openPreview(readQrValue(data));
    },
    [openPreview],
  );

  const resumeScanner = () => {
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      scanLockRef.current = false;
      setScannerKey((current) => current + 1);
      setScannerActive(true);
    }, 250);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setScannedValue("");
    resumeScanner();
  };

  const handleConfirmed = (result) => {
    const payload = getPayload(result.serverResponse);
    const ticket = payload?.bookingData?.ticket_instance;
    setLastResult({
      message: result.idempotentReplay
        ? "This ticket was already accepted by the previous request."
        : payload?.message || "Entry confirmed.",
      ticketName: ticket?.ticketName,
      seatNo: ticket?.seatNo,
      scannedNow: result.scannedNow,
    });
    setPreviewOpen(false);
    setScannedValue("");
    resumeScanner();
  };

  const submitManualCode = (event) => {
    event.preventDefault();
    openPreview(manualValue);
    setManualValue("");
  };

  const dashboardPath =
    contentId && ["event", "movie"].includes(contentType)
      ? `/scanner/dashboard/${contentType}/${contentId}`
      : "/scanner/dashboard";

  return (
    <>
      <MetaData title="Ticket Scanner | TicketVerz" />

      <div className="mx-auto max-w-4xl space-y-5">
        <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="flex flex-col gap-4 border-b border-gray-100 px-5 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <QrCodeIcon className="h-6 w-6 text-[#1d275f]" />
                <h1 className="text-xl font-bold text-gray-950">Ticket scanner</h1>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                {contentTitle
                  ? `Checking in guests for ${contentTitle}`
                  : "Scan an individual ticket QR or a supported legacy booking QR."}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(dashboardPath)}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-[#1d275f]/20 bg-[#1d275f]/5 px-4 py-2.5 text-sm font-bold text-[#1d275f] hover:bg-[#1d275f]/10"
            >
              <ChartBarSquareIcon className="h-5 w-5" />
              Check-in dashboard
            </button>
          </div>

          <div className="p-4 sm:p-6">
            {lastResult && (
              <div className="mb-5 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-900">
                <CheckCircleIcon className="h-6 w-6 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold">{lastResult.message}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <p className="break-words text-xl font-black">
                      {lastResult.ticketName || "Legacy booking"}
                    </p>
                    {lastResult.seatNo && (
                      <span className="rounded-md bg-white px-3 py-1.5 text-base font-black text-[#1d275f] shadow-sm">
                        Seat {lastResult.seatNo}
                      </span>
                    )}
                    {lastResult.scannedNow > 1 && (
                      <span className="text-sm font-bold">
                        {lastResult.scannedNow} guests
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {cameraError && (
              <div className="mb-5 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
                <p className="text-sm font-semibold">{cameraError}</p>
              </div>
            )}

            {scannerActive ? (
              <div>
                <div className="mx-auto max-w-xl overflow-hidden rounded-lg bg-gray-950 ring-1 ring-gray-200">
                  <Scanner
                    key={scannerKey}
                    onScan={handleScan}
                    allowMultiple={false}
                    scanDelay={1800}
                    onError={(error) => {
                      setCameraError(
                        error?.message ||
                          "Camera unavailable. Check browser camera permission.",
                      );
                      setScannerActive(false);
                    }}
                    constraints={{ facingMode: "environment" }}
                    components={{
                      audio: true,
                      torch: true,
                      zoom: true,
                      finder: true,
                    }}
                  />
                </div>
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={stopScanner}
                    className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-bold text-red-700 hover:bg-red-100"
                  >
                    <StopIcon className="h-5 w-5" />
                    Stop camera
                  </button>
                </div>
              </div>
            ) : (
              <div className="mx-auto max-w-xl rounded-lg border border-dashed border-gray-300 bg-gray-50 px-5 py-10 text-center">
                <QrCodeIcon className="mx-auto h-12 w-12 text-gray-300" />
                <p className="mt-3 font-bold text-gray-900">
                  Camera scanner is paused
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  Camera access is used only while the scanner is active.
                </p>
                <button
                  type="button"
                  onClick={startScanner}
                  className="mt-5 inline-flex items-center gap-2 rounded-md bg-[#1d275f] px-5 py-3 text-sm font-bold text-white hover:bg-[#151d49]"
                >
                  <ArrowPathIcon className="h-5 w-5" />
                  Start camera
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
          <div className="flex items-center gap-2">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-500" />
            <h2 className="font-bold text-gray-950">Manual QR lookup</h2>
          </div>
          <form
            onSubmit={submitManualCode}
            className="mt-3 flex flex-col gap-3 sm:flex-row"
          >
            <input
              value={manualValue}
              onChange={(event) => setManualValue(event.target.value)}
              placeholder="Enter tkt_... or legacy booking ID"
              className="min-w-0 flex-1 rounded-md border border-gray-300 px-4 py-3 text-sm outline-none focus:border-[#1d275f] focus:ring-2 focus:ring-[#1d275f]/10"
            />
            <button
              type="submit"
              disabled={!manualValue.trim()}
              className="rounded-md bg-gray-900 px-5 py-3 text-sm font-bold text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Verify
            </button>
          </form>
        </section>
      </div>

      <ScanPreviewConfirmModal
        isOpen={previewOpen}
        qrCodeData={scannedValue}
        onClose={closePreview}
        onConfirmed={handleConfirmed}
      />
    </>
  );
}
